import { useMemo } from "react";
import { useMedia } from "react-use";
import { css, styled } from "styled-components";
import { theme } from "layout";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import checkIconSvg from "constants/modelCompareData/assets/check.svg";
import minusIconSvg from "constants/modelCompareData/assets/minus.svg";

import type { 
  MutatedProperty, 
  MutatedPropertyValue } from "constants/modelCompareData/types";
import { MutatedPropertyType } from "constants/modelCompareData/types";
// import { theme } from "@ubnt/store.core/lib/constants/theme";
import { useMenu } from "@ubnt/store.core/lib/contexts/MenuContext";
import { Reveal } from "@ubnt/store.core/lib/components/Reveal";
import { StyleableSvgImage } from "@ubnt/store.core/lib/components/StyleableSvgImage";
import { MOBILE_BREAKPOINT } from "../../CompareTable.styles";
import { StickyGridItem } from "./StickyGrid";

export function PropertyValueGridItem({
  productProperty,
  includeProperty,
  property,
  isLastProduct,
  index,
}: {
  productProperty?: MutatedPropertyValue;
  includeProperty: boolean;
  property: MutatedProperty;
  isLastProduct: boolean;
  index: number;
}) {
  const isMobile = useMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

  if (includeProperty) {
    return (
      <InlineGridItem>
        {isMobile ? (
          <>
            <PropertyIcon>
              <StyleableSvgImage width={30} height={30} src={property.icon} />
            </PropertyIcon>
            <PropertyTitle>{property.title}</PropertyTitle>
            <Spacer />
            <Value
              $disableOverflow={
                productProperty?.value.type === MutatedPropertyType.Boolean
              }
            >
              <PropertyValueComponent productProperty={productProperty} />
            </Value>
          </>
        ) : (
          <Reveal index={index}>
            <PropertyIcon>
              <StyleableSvgImage width={30} height={30} src={property.icon} />
            </PropertyIcon>
            <PropertyTitle>{property.title}</PropertyTitle>
            <Spacer />
            <Value
              $disableOverflow={
                productProperty?.value.type === MutatedPropertyType.Boolean
              }
            >
              <PropertyValueComponent productProperty={productProperty} />
            </Value>
          </Reveal>
        )}
      </InlineGridItem>
    );
  }

  return (
    <GridItem $isLastProduct={isLastProduct}>
      <Reveal index={index}>
        <PropertyValueComponent productProperty={productProperty} />
      </Reveal>
    </GridItem>
  );
}

function PropertyValueComponent({
  productProperty,
}: {
  productProperty?: MutatedPropertyValue;
}) {
  const { languageDirection, storeId } = useMenu();

  const asterisks = useMemo(() => {
    const count = productProperty?.value.asterisk || 0;
    return count > 0 ? "*".repeat(count) : null;
  }, [productProperty?.value.asterisk]);

  if (!productProperty?.value) return null;

  const { type, value } = productProperty.value;

  switch (type) {
    case MutatedPropertyType.Custom:
      return (
        <>
          {value}
          {asterisks}
        </>
      );

    case MutatedPropertyType.Boolean:
      return (
        <BooleanWrap>
          <StyleableSvgImage
            width={value ? 16 : 11}
            height={value ? 12 : 8}
            src={value ? checkIconSvg : minusIconSvg}
          />
          {asterisks && <BooleanAsterisks>{asterisks}</BooleanAsterisks>}
        </BooleanWrap>
      );

    case MutatedPropertyType.String: {
      const value =
        typeof productProperty.value.value === "string"
          ? productProperty.value.value
          : productProperty.value.value(storeId);

      return (
        <Stack>
          {value.split("\n").map((row, i, arr) => {
            const isLastRow = i === arr.length - 1;
            return (
              <div dir={languageDirection} key={i}>
                {row}
                {isLastRow && asterisks}
              </div>
            );
          })}
        </Stack>
      );
    }
    default:
      return null;
  }
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 16.8px;
  letter-spacing: -0.28px;
  color: ${theme.colors.black65};

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 12px;
  }
`;

const Value = styled.div<{ $disableOverflow?: boolean }>`
  text-align: right;

  ${({ $disableOverflow }) =>
    !$disableOverflow &&
    css`
      overflow: hidden;
    `}
  text-overflow: ellipsis;
  min-width: min-content;

  div {
    ${({ $disableOverflow }) =>
      !$disableOverflow &&
      css`
        overflow: hidden;
      `}

    text-overflow: ellipsis;
  }
`;

const borderStyles = `1px solid ${theme.colors.athensGreyDark}`;

const GridItem = styled(StickyGridItem)<{ $isLastProduct: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #000000a6;
  text-align: center;
  padding: 16px;
  border-bottom: ${borderStyles};
  border-left: ${borderStyles};

  ${({ $isLastProduct }) =>
    $isLastProduct &&
    css`
      border-right: ${borderStyles};
    `}
`;

const InlineGridItem = styled(StickyGridItem)`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 16px;
  overflow: hidden;
  border-bottom: 1px solid ${theme.colors.athensGreyDark};
`;

const PropertyTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  max-width: 45%;
  line-height: ${17 / 14};
`;

const PropertyIcon = styled.div`
  width: 30px;
  height: 30px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const BooleanWrap = styled.div`
  position: relative;
  overflow: visible;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5.5px;
  }
`;

const BooleanAsterisks = styled.div`
  position: absolute;
  font-size: 14px;
  line-height: 16.8px;

  top: -4.5px;
  left: calc(100% + 5.5px);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: static;

    margin-top: -4.5px;
  }
`;
